import { computed, toValue } from 'vue';
import { isEmpty, isNil } from 'lodash';

import type { MaybeRefOrGetter } from 'vue';

/** What the entity selects bind to `v-model`: one entity, many, or nothing. */
export type EntitySelectModelValue<TItem> = TItem | TItem[] | null;

export interface EntitySelectionSummary {
  /** Text shown in the field: the item's own title, or the select's noun for the count. */
  text: string;
  /** Count for the inline badge; `null` when no badge should render. */
  count: number | null;
}

export interface UseEntitySelectModelOptions<TItem extends { id: string }> {
  modelValue: MaybeRefOrGetter<EntitySelectModelValue<TItem>>;
  /**
   * The selectable entities — never the grouped row list, or "select all" would
   * put headers and dividers into the model.
   */
  items: MaybeRefOrGetter<readonly TItem[]>;
  multiple: MaybeRefOrGetter<boolean>;
  /** Field label for a single selected entity. */
  getItemTitle: (item: TItem) => string;
  /**
   * Noun for a counted selection, e.g. `count => t('global.specialtyTerms.loadTypes', count)`.
   * The count is passed through so the caller's i18n applies its own plural rules — this
   * composable has no business deciding what counts as plural in a given language.
   */
  getCountLabel: (count: number) => string;
  onUpdate: (value: EntitySelectModelValue<TItem>) => void;
}

/**
 * Multi-select instrumentation shared by the entity autocompletes (carrier, company,
 * load type): the 0 / 1 / 2+ selection summary, select-all / only, checkbox icons, and
 * normalization between the single and multiple model shapes.
 *
 * These selects use `return-object`, so the model holds whole entities rather than ids.
 * Ids are still what selection compares on, since a re-fetch hands back new object
 * identities for the same rows.
 */
export function useEntitySelectModel<TItem extends { id: string }>(
  options: UseEntitySelectModelOptions<TItem>
) {
  const isMultiple = computed(() => Boolean(toValue(options.multiple)));
  const items = computed(() => [...toValue(options.items)]);

  const selectedItems = computed((): TItem[] => {
    const currentValue = toValue(options.modelValue);

    if (isNil(currentValue)) {
      return [];
    }

    return Array.isArray(currentValue) ? currentValue.filter(Boolean) : [currentValue];
  });

  const selectedIds = computed(() => selectedItems.value.map(item => item.id));
  const selectedIdSet = computed(() => new Set(selectedIds.value));

  /** Vuetify wants an array when `multiple`, and a single entity or `null` otherwise. */
  const autocompleteModelValue = computed(() =>
    isMultiple.value ? selectedItems.value : (selectedItems.value[0] ?? null)
  );

  /**
   * Nothing selected renders no text (the placeholder shows through), one renders that
   * entity's title, and two or more render the counted noun with the count in a badge.
   *
   * A selection with no matching row (narrowed away, or options still loading) keeps the
   * noun plus a count of 1 rather than rendering empty.
   */
  const selectionSummary = computed<EntitySelectionSummary>(() => {
    if (isEmpty(selectedIds.value)) {
      return { text: '', count: null };
    }

    if (selectedIds.value.length === 1) {
      const selectedId = selectedIds.value[0]!;
      const matchedItem = items.value.find(item => item.id === selectedId);
      const title = matchedItem ? options.getItemTitle(matchedItem) : '';

      return title ? { text: title, count: null } : { text: options.getCountLabel(1), count: 1 };
    }

    return {
      text: options.getCountLabel(selectedIds.value.length),
      count: selectedIds.value.length
    };
  });

  const isAllSelected = computed(() => {
    if (!isMultiple.value || isEmpty(items.value)) {
      return false;
    }

    return items.value.every(item => selectedIdSet.value.has(item.id));
  });

  const selectAllIcon = computed(() => {
    if (isAllSelected.value) {
      return 'mdi-close-box';
    }

    return isEmpty(selectedIds.value) ? 'mdi-checkbox-blank-outline' : 'mdi-minus-box';
  });

  const isItemSelected = (item: TItem) => selectedIdSet.value.has(item.id);

  const getItemIcon = (item: TItem) =>
    isItemSelected(item) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline';

  const toggleSelectAll = () => {
    if (!isMultiple.value) {
      return;
    }

    options.onUpdate(isAllSelected.value ? [] : [...items.value]);
  };

  /** Narrows a multi-selection down to the clicked row without closing the menu. */
  const selectOnly = (event: Event, item: TItem) => {
    event.stopPropagation();

    if (!isMultiple.value) {
      return;
    }

    options.onUpdate([item]);
  };

  /**
   * Vuetify emits an entity, an array of them, or `null`. Single-select keeps only the
   * first so a stale array from a `multiple` toggle cannot leak through.
   */
  const onSelectionUpdate = (value: unknown) => {
    const nextItems = (Array.isArray(value) ? value : [value]).filter(
      (item): item is TItem => !isNil(item)
    );

    options.onUpdate(isMultiple.value ? nextItems : (nextItems[0] ?? null));
  };

  return {
    autocompleteModelValue,
    getItemIcon,
    isAllSelected,
    isItemSelected,
    isMultiple,
    onSelectionUpdate,
    selectAllIcon,
    selectedItems,
    selectionSummary,
    selectOnly,
    toggleSelectAll
  };
}
